/**
 * Delete (slice 5b spec §8): on an archived doc, for its maker or the owner,
 * behind a confirm. Gone for good, a file doc's file with it; the page goes
 * back to Archived.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button } from "@crew/ui/components/button";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useDeleteDoc, type Doc } from "../docs/api";

/** Whether this person may delete the doc: its maker, or the owner (an agent's, the owner only). */
export function canDelete(doc: Pick<Doc, "created_by" | "archived_at">, me: Pick<Me, "user">) {
  if (!doc.archived_at) return false;
  if (me.user.role === "owner") return true;
  return doc.created_by.kind === "person" && doc.created_by.id === me.user.id;
}

export function DeleteDoc({ doc, me, size = "sm" }: { doc: Doc; me: Me; size?: "sm" | "default" }) {
  const [open, setOpen] = useState(false);
  const deleting = useDeleteDoc(doc.id);
  const navigate = useNavigate();
  if (!canDelete(doc, me)) return null;
  return (
    <>
      <Button size={size} variant="destructive" onClick={() => setOpen(true)} data-doc-delete>
        <Trash2 />
        Delete
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {doc.title} for good?</AlertDialogTitle>
            <AlertDialogDescription>
              {doc.file
                ? "The crew can't get it back, and its file goes with it."
                : "The crew can't get it back."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleting.error ? (
            <p role="alert" className="text-meta text-brick">
              {errorHint(deleting.error)}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={deleting.isPending}
              data-doc-delete-confirm
              onClick={() =>
                deleting.mutate(undefined, {
                  onSuccess: () => {
                    setOpen(false);
                    void navigate("/docs?tab=archived", { replace: true });
                  },
                })
              }
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
